/**
 * For the Facebook About settings, each setting corresponds to a section, e.g.,
 * "Family" corresponds to the family section on
 * https://www.facebook.com/{profile-name}/about_family_and_relationships
 *
 * We fetch all rows in each section, and check their privacy settings to make
 * sure they are as strict as we recommend.
 *
 * Since each row is labelled by PII (such as the name of a family member), we
 * hash the value of these labels to make them identifiable, but also obscured.
 */

import {
  FacebookSettingKey,
  SettingKey,
} from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { digestMessage } from '$extensionSrc/utils/crypto'
import {
  FB_PRIVACY_LEVEL,
  FBRecommendationKey,
  StageType,
} from '$extensionSrc/utils/enums'

import { StageDef, UserContext } from '../automationMachineInterfaces'
import { evaluateXpath } from '../interruptibleXpath'
import {
  getCantUndoFnFromKey,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement, shouldUpdateSetting } from '../utils'
import { getCaseInsensitiveText } from '../xpath'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  allAboutSectionValuesEqual,
  checkCantUndoPrivacySetting,
  getUniqueAboutSectionValues,
} from './facebookRecommendationCardDisplayUtils'
import {
  clickAndWaitForModalToOpen,
  clickTargetPrivacySettingOptionAndSave,
  closeModal,
  getEditPrivacyIcons,
  getSelectedPrivacyLevel,
  ONLY_ME,
  PrivacySetting,
} from './fbSelectAudienceModalUtils'
import {
  aboutContactAndBasicInfoUrl,
  aboutEducationUrl,
  aboutPersonalDetailsUrl,
  aboutWorkUrl,
} from './fbUrls'

export type AboutRowData = {
  labels: string[]
  setting: PrivacySetting
}

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (records: AboutRowData[]) => {
    if (records.length === 0) {
      return 'Not provided'
    }
    return getUniqueAboutSectionValues(records).join(', ')
  })
const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (records: AboutRowData[]) => {
    if (records.length === 0) {
      return true
    }
    return allAboutSectionValuesEqual(records, FB_PRIVACY_LEVEL.ONLY_ME)
  })
const getCantUndoFn = (key) =>
  getCantUndoFnFromKey(key, (records: AboutRowData[]) =>
    checkCantUndoPrivacySetting(records),
  )

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

/**
 * Wait for "About" header to become visible; we interpret that as the UX
 * having loaded successfully.
 */
async function waitForPageToLoad(): Promise<void> {
  await getElement(
    document,
    `//h2[count(descendant::*[${getCaseInsensitiveText()} = "about"]) > 0]`,
  )
}

async function getAboutSettingsBox(): Promise<HTMLElement> {
  return getElement<HTMLElement>(
    document,
    `//*[` +
      // Include the "about" and "overview" text spans
      `  count(descendant::*[${getCaseInsensitiveText()} = "about"]) > 0 and ` +
      `  count(descendant::*[${getCaseInsensitiveText()} = "intro"]) > 0 and` +
      // Exclude the "friends" section
      `  count(descendant::*[${getCaseInsensitiveText()} = "friends"]) = 0` +
      `]`,
  )
}

/**
 * Find a <div> that corresponds to a section of settings. Note that this only
 * works if there are at least two sections on the page; works by finding divs
 * that contain one section header but not the other.
 */
async function getSection(
  sectionName: string,
  otherSectionName: string,
): Promise<HTMLElement> {
  return getElement<HTMLElement>(
    document,
    `//*[` +
      // Include the "about" and "overview" text spans
      `  count(descendant::h2/descendant::*[${getCaseInsensitiveText()} = "${sectionName.toLocaleLowerCase()}"]) > 0 and ` +
      // Exclude the other section
      `  count(descendant::h2/descendant::*[${getCaseInsensitiveText()} = "${otherSectionName.toLocaleLowerCase()}"]) = 0` +
      `]`,
  )
}

function getRows(parentElement: HTMLElement): HTMLElement[] {
  const icons = getEditPrivacyIcons(parentElement, {
    includeDisabled: false,
  })

  return (
    icons
      // Get rows associated with each edit privacy row
      .map((x) => {
        // Get all ancestors with more than one text descendant,
        // and select the last one (sorted by document order)
        //
        // Ignore header items to have correct behavior in the event no labels are
        // present
        const candidates = evaluateXpath<HTMLElement>(
          x,
          'ancestor::*[count(./descendant::*[text()]) > 1 and count(./descendant::h2) = 0]',
          { requireDocumentOrder: true },
        )

        if (candidates.length === 0) {
          return undefined
        }

        return candidates[candidates.length - 1]
      })
      // Filter out undefined cases
      .filter((x) => Boolean(x))
  )
}

export async function getAllRows(): Promise<HTMLElement[]> {
  await waitForPageToLoad()

  const aboutSettingsBox = await getAboutSettingsBox()

  return getRows(aboutSettingsBox)
}

export async function getRowsInSection(
  sectionName: string,
  otherSectionName: string,
): Promise<HTMLElement[]> {
  await waitForPageToLoad()

  const section = await getSection(sectionName, otherSectionName)

  return getRows(section)
}

export async function getRowWithLabelOptions(
  textOptions: string[],
): Promise<HTMLElement[]> {
  const rows = await getAllRows()

  const xpathClause = textOptions
    .map((x) => `${getCaseInsensitiveText()}="${x.toLocaleLowerCase()}"`)
    .join(' or ')
  const candidates = rows.filter(
    (row) =>
      evaluateXpath(row, `descendant::*[${xpathClause}]`).length > 0 &&
      // Ensure edit audience icon not disabled
      getEditPrivacyIcons(row).length > 0,
  )

  if (candidates.length === 0) {
    return []
  }
  // Do we want to special case if multiple candidates?
  return [candidates[0]]
}

function getRowLabels(row: HTMLElement): string[] {
  const labels = evaluateXpath<HTMLElement>(row, 'descendant::*[text()]')
  const texts = labels
    .map((x) => x.textContent)
    .filter((x) => !x.toLocaleLowerCase().startsWith('shared with'))

  if (texts.length === 0) {
    throw new Error('getRowLabels: no labels found')
  }

  return texts
}

async function openModalForRow(row: HTMLElement): Promise<boolean> {
  const editPrivacyIcon = getEditPrivacyIcons(row, {
    includeDisabled: false,
  })[0]

  if (!editPrivacyIcon) {
    throw new Error(
      `openModalForRow: Could not find element for edit audience icon in row`,
    )
  }

  let success = await clickAndWaitForModalToOpen(editPrivacyIcon)
    .then(() => true)
    .catch(() => false)

  if (!success) {
    success = await clickAndWaitForModalToOpen(editPrivacyIcon)
      .then(() => true)
      .catch(() => false)
  }

  return success
}

async function getPrivacySettingFromModal(options: {
  removeModalManually: boolean
}): Promise<PrivacySetting> {
  const privacySetting = await getSelectedPrivacyLevel()
  await closeModal('CANCEL', options)
  return privacySetting
}

async function getRowPrivacySetting(
  row: HTMLElement,
  options: { removeModalManually: boolean },
): Promise<PrivacySetting> {
  const openModalSuccess = await openModalForRow(row)

  if (!openModalSuccess) {
    throw new Error('getRowPrivacySetting: modal did not open')
  }

  return getPrivacySettingFromModal(options)
}

export async function getRowData(
  row: HTMLElement,
  options: { removeModalManually: boolean },
): Promise<AboutRowData> {
  const labels = getRowLabels(row)
  const setting = await getRowPrivacySetting(row, options)

  return { labels, setting }
}

/**
 * Important to apply to all rows at once so that in the event of duplicates
 * we can tie break with index.
 */
export async function getHashedData(
  data: AboutRowData[],
): Promise<AboutRowData[]> {
  const result: AboutRowData[] = []
  const counter: { [key: string]: number } = {}
  for (const datum of data) {
    const concat = datum.labels.join('')

    const prevCount = counter[concat]
    const curCount = prevCount === undefined ? 0 : prevCount + 1

    const hash = await digestMessage(`${concat}|||${curCount}`)

    result.push({
      labels: [hash],
      setting: datum.setting,
    })

    counter[concat] = curCount
  }
  return result
}

export async function gatherWrapper(
  userContext: Pick<
    UserContext,
    'updateStoredInitialValues' | 'updateStoredAutomationErrors'
  >,
  rowsPromise: Promise<HTMLElement[]>,
  settingKey: FacebookSettingKey,
): Promise<void> {
  try {
    const rows = await rowsPromise

    // Process rows one-by-one (since we need to open modals to get data)
    const data: AboutRowData[] = []
    for (const row of rows) {
      data.push(await getRowData(row, { removeModalManually: true }))
    }

    const hashedData = await getHashedData(data)

    await userContext.updateStoredInitialValues(
      {
        [settingKey]: hashedData,
      },
      FBRecommendationKey.FB_ABOUT_SETTINGS,
    )
  } catch (error) {
    // Do we want to log the errors to sentry, too?
    await userContext.updateStoredAutomationErrors(
      {
        [settingKey]: {
          gatherError: { message: error.message, name: error.name },
        },
      },
      FBRecommendationKey.FB_ABOUT_SETTINGS,
    )
  }
}

/**
 * Note: does not check the labels on each row; just does every row the row
 * promise returns.
 */
export async function updateWrapper(
  userContext: Pick<
    UserContext,
    'settingsSelected' | 'initialValues' | 'isDry'
  >,
  rowsPromise: Promise<HTMLElement[]>,
  settingKey: FacebookSettingKey,
  targetValue: PrivacySetting,
): Promise<void> {
  if (
    !shouldUpdateSetting(
      userContext.settingsSelected,
      userContext.initialValues,
      settingKey,
      getIsFulfilledFn,
    )
  ) {
    return
  }

  const rows = await rowsPromise

  for (const row of rows) {
    const openModalSuccess = await openModalForRow(row)

    if (!openModalSuccess) {
      throw new Error('update: modal did not open')
    }

    await clickTargetPrivacySettingOptionAndSave(userContext.isDry, targetValue)
  }
}

function getSettingFromInitialState(
  initialValues: Partial<Record<SettingKey, any>>,
  settingKey: SettingKey,
  currentLabels: string[],
): PrivacySetting | undefined {
  const storedValues = initialValues?.[settingKey] || []

  const isMatch = (record) => {
    const storedLabel = record.labels || []

    const labelLen = storedLabel.length
    const storedLabelAsStr = storedLabel.join('@')
    const currentLabelAsStr = (currentLabels || []).slice(0, labelLen).join('@')

    return storedLabelAsStr === currentLabelAsStr
  }

  return storedValues.filter(isMatch)[0]?.setting
}

async function undoWrapper(
  userContext: UserContext,
  rowsPromise: Promise<HTMLElement[]>,
  settingKey: FacebookSettingKey,
): Promise<void> {
  if (
    !shouldUpdateSetting(
      userContext.settingsSelected,
      userContext.initialValues,
      settingKey,
      getIsFulfilledFn,
    )
  ) {
    return
  }

  const rows = await rowsPromise

  // Process rows one-by-one (since we need to open modals to get data)
  const data: AboutRowData[] = []
  for (const row of rows) {
    data.push(await getRowData(row, { removeModalManually: false }))
  }

  const hashedData = await getHashedData(data)

  for (let ix = 0; ix < rows.length; ix += 1) {
    const curRow = rows[ix]
    const curHash = hashedData[ix]

    if (!curRow || !curHash) {
      throw new Error('undo: Could not find row to undo')
    }

    const target = getSettingFromInitialState(
      userContext.initialValues,
      settingKey,
      curHash.labels,
    )

    if (!target) {
      throw new Error('undo: Could not get target value for row')
    }

    const openModalSuccess = await openModalForRow(curRow)
    if (!openModalSuccess) {
      throw new Error('undo: privacy modal did not open')
    }

    await clickTargetPrivacySettingOptionAndSave(userContext.isDry, target)
  }
}

enum StageKey {
  GATHER_SETTING_ABOUT_CONTACT_INFO = 'GATHER_SETTING_ABOUT_CONTACT_INFO',
  GATHER_SETTING_ABOUT_BIRTH_DAY = 'GATHER_SETTING_ABOUT_BIRTH_DAY',
  GATHER_SETTING_ABOUT_PLACES = 'GATHER_SETTING_ABOUT_CURRENT_PLACES',
  GATHER_SETTING_ABOUT_WORK = 'GATHER_SETTING_ABOUT_WORK',
  GATHER_SETTING_ABOUT_EDUCATION = 'GATHER_SETTING_ABOUT_EDUCATION',
  GATHER_SETTING_ABOUT_PEOPLE = 'GATHER_SETTING_ABOUT_PEOPLE',
  UPDATE_SETTING_ABOUT_CONTACT_INFO = 'UPDATE_SETTING_ABOUT_CONTACT_INFO',
  UPDATE_SETTING_ABOUT_BIRTH_DAY = 'UPDATE_SETTING_ABOUT_BIRTH_DAY',
  UPDATE_SETTING_ABOUT_PLACES = 'UPDATE_SETTING_ABOUT_CURRENT_PLACES',
  UPDATE_SETTING_ABOUT_WORK = 'UPDATE_SETTING_ABOUT_WORK',
  UPDATE_SETTING_ABOUT_EDUCATION = 'UPDATE_SETTING_ABOUT_EDUCATION',
  UPDATE_SETTING_ABOUT_PEOPLE = 'UPDATE_SETTING_ABOUT_PEOPLE',
  UNDO_SETTING_ABOUT_CONTACT_INFO = 'UNDO_SETTING_ABOUT_CONTACT_INFO',
  UNDO_SETTING_ABOUT_BIRTH_DAY = 'UNDO_SETTING_ABOUT_BIRTH_DAY',
  UNDO_SETTING_ABOUT_PLACES = 'UNDO_SETTING_ABOUT_CURRENT_PLACES',
  UNDO_SETTING_ABOUT_WORK = 'UNDO_SETTING_ABOUT_WORK',
  UNDO_SETTING_ABOUT_EDUCATION = 'UNDO_SETTING_ABOUT_EDUCATION',
  UNDO_SETTING_ABOUT_PEOPLE = 'UNDO_SETTING_ABOUT_PEOPLE',
}

function buildLoggedInUrlRegex(url: string): string {
  const urlWithProfileId = url.replace('profile.php?', '.*')
  return urlWithProfileId
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTING_ABOUT_CONTACT_INFO,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: aboutContactAndBasicInfoUrl,
    urlPattern: buildLoggedInUrlRegex(aboutContactAndBasicInfoUrl),
    action: async (machineContext, userContext) => {
      await gatherWrapper(
        userContext,
        getRowWithLabelOptions(['Email']),
        FacebookSettingKey.ABOUT_EMAIL,
      )

      await gatherWrapper(
        userContext,
        getRowWithLabelOptions(['Mobile']),
        FacebookSettingKey.ABOUT_PHONE_NUMBER,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTING_ABOUT_WORK,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: aboutWorkUrl,
    urlPattern: buildLoggedInUrlRegex(aboutWorkUrl),
    action: async (machineContext, userContext) => {
      await gatherWrapper(
        userContext,
        getAllRows(),
        FacebookSettingKey.ABOUT_WORK,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTING_ABOUT_EDUCATION,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: aboutEducationUrl,
    urlPattern: buildLoggedInUrlRegex(aboutEducationUrl),
    action: async (machineContext, userContext) => {
      await gatherWrapper(
        userContext,
        getAllRows(),
        FacebookSettingKey.ABOUT_EDUCATION,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTING_ABOUT_BIRTH_DAY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: aboutPersonalDetailsUrl,
    urlPattern: buildLoggedInUrlRegex(aboutPersonalDetailsUrl),
    action: async (machineContext, userContext) => {
      await gatherWrapper(
        userContext,
        getRowWithLabelOptions(['Birth date']),
        FacebookSettingKey.ABOUT_BIRTH_DATE,
      )

      await gatherWrapper(
        userContext,
        getRowWithLabelOptions(['Birth year']),
        FacebookSettingKey.ABOUT_BIRTH_YEAR,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTING_ABOUT_PLACES,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: aboutPersonalDetailsUrl,
    urlPattern: buildLoggedInUrlRegex(aboutPersonalDetailsUrl),
    action: async (machineContext, userContext) => {
      await gatherWrapper(
        userContext,
        getRowWithLabelOptions(['Current city', 'Current town/city']),
        FacebookSettingKey.ABOUT_CURRENT_CITY,
      )

      await gatherWrapper(
        userContext,
        getRowWithLabelOptions(['Hometown', 'Home town']),
        FacebookSettingKey.ABOUT_HOMETOWN,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTING_ABOUT_PEOPLE,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: aboutPersonalDetailsUrl,
    urlPattern: buildLoggedInUrlRegex(aboutPersonalDetailsUrl),
    action: async (machineContext, userContext) => {
      await gatherWrapper(
        userContext,
        getRowsInSection('Family members', 'Location'),
        FacebookSettingKey.ABOUT_FAMILY,
      )

      await gatherWrapper(
        userContext,
        getRowsInSection('Status', 'Location'),
        FacebookSettingKey.ABOUT_RELATIONSHIPS,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTING_ABOUT_CONTACT_INFO,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: aboutContactAndBasicInfoUrl,
    urlPattern: buildLoggedInUrlRegex(aboutContactAndBasicInfoUrl),
    action: async (machineContext, userContext) => {
      await updateWrapper(
        userContext,
        getRowWithLabelOptions(['Email']),
        FacebookSettingKey.ABOUT_EMAIL,
        ONLY_ME,
      )

      await updateWrapper(
        userContext,
        getRowWithLabelOptions(['Mobile']),
        FacebookSettingKey.ABOUT_PHONE_NUMBER,
        ONLY_ME,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTING_ABOUT_WORK,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: aboutWorkUrl,
    urlPattern: buildLoggedInUrlRegex(aboutWorkUrl),
    action: async (machineContext, userContext) => {
      await updateWrapper(
        userContext,
        getAllRows(),
        FacebookSettingKey.ABOUT_WORK,
        ONLY_ME,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTING_ABOUT_EDUCATION,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: aboutEducationUrl,
    urlPattern: buildLoggedInUrlRegex(aboutEducationUrl),
    action: async (machineContext, userContext) => {
      await updateWrapper(
        userContext,
        getAllRows(),
        FacebookSettingKey.ABOUT_EDUCATION,
        ONLY_ME,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTING_ABOUT_BIRTH_DAY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: aboutPersonalDetailsUrl,
    urlPattern: buildLoggedInUrlRegex(aboutContactAndBasicInfoUrl),
    action: async (machineContext, userContext) => {
      await updateWrapper(
        userContext,
        getRowWithLabelOptions(['Birth date']),
        FacebookSettingKey.ABOUT_BIRTH_DATE,
        ONLY_ME,
      )

      await updateWrapper(
        userContext,
        getRowWithLabelOptions(['Birth year']),
        FacebookSettingKey.ABOUT_BIRTH_YEAR,
        ONLY_ME,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTING_ABOUT_PLACES,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: aboutPersonalDetailsUrl,
    urlPattern: buildLoggedInUrlRegex(aboutPersonalDetailsUrl),
    action: async (machineContext, userContext) => {
      await updateWrapper(
        userContext,
        getRowWithLabelOptions(['Current city', 'Current town/city']),
        FacebookSettingKey.ABOUT_CURRENT_CITY,
        ONLY_ME,
      )

      await updateWrapper(
        userContext,
        getRowWithLabelOptions(['Hometown', 'Home town']),
        FacebookSettingKey.ABOUT_HOMETOWN,
        ONLY_ME,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTING_ABOUT_PEOPLE,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: aboutPersonalDetailsUrl,
    urlPattern: buildLoggedInUrlRegex(aboutPersonalDetailsUrl),
    action: async (machineContext, userContext) => {
      await updateWrapper(
        userContext,
        getRowsInSection('Family members', 'Location'),
        FacebookSettingKey.ABOUT_FAMILY,
        ONLY_ME,
      )

      await updateWrapper(
        userContext,
        getRowsInSection('Status', 'Location'),
        FacebookSettingKey.ABOUT_RELATIONSHIPS,
        ONLY_ME,
      )
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTING_ABOUT_CONTACT_INFO,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: aboutContactAndBasicInfoUrl,
    urlPattern: buildLoggedInUrlRegex(aboutContactAndBasicInfoUrl),
    action: async (machineContext, userContext) => {
      await undoWrapper(
        userContext,
        getRowWithLabelOptions(['Email']),
        FacebookSettingKey.ABOUT_EMAIL,
      )

      await undoWrapper(
        userContext,
        getRowWithLabelOptions(['Mobile']),
        FacebookSettingKey.ABOUT_PHONE_NUMBER,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTING_ABOUT_WORK,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: aboutWorkUrl,
    urlPattern: buildLoggedInUrlRegex(aboutWorkUrl),
    action: async (machineContext, userContext) => {
      await undoWrapper(
        userContext,
        getAllRows(),
        FacebookSettingKey.ABOUT_WORK,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTING_ABOUT_EDUCATION,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: aboutEducationUrl,
    urlPattern: buildLoggedInUrlRegex(aboutEducationUrl),
    action: async (machineContext, userContext) => {
      await undoWrapper(
        userContext,
        getAllRows(),
        FacebookSettingKey.ABOUT_EDUCATION,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTING_ABOUT_BIRTH_DAY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: aboutPersonalDetailsUrl,
    urlPattern: buildLoggedInUrlRegex(aboutContactAndBasicInfoUrl),
    action: async (machineContext, userContext) => {
      await undoWrapper(
        userContext,
        getRowWithLabelOptions(['Birth date']),
        FacebookSettingKey.ABOUT_BIRTH_DATE,
      )

      await undoWrapper(
        userContext,
        getRowWithLabelOptions(['Birth year']),
        FacebookSettingKey.ABOUT_BIRTH_YEAR,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTING_ABOUT_PLACES,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: aboutPersonalDetailsUrl,
    urlPattern: buildLoggedInUrlRegex(aboutPersonalDetailsUrl),
    action: async (machineContext, userContext) => {
      await undoWrapper(
        userContext,
        getRowWithLabelOptions(['Current city', 'Current town/city']),
        FacebookSettingKey.ABOUT_CURRENT_CITY,
      )

      await undoWrapper(
        userContext,
        getRowWithLabelOptions(['Hometown', 'Home town']),
        FacebookSettingKey.ABOUT_HOMETOWN,
      )
    },
  },

  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTING_ABOUT_PEOPLE,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: aboutPersonalDetailsUrl,
    urlPattern: buildLoggedInUrlRegex(aboutPersonalDetailsUrl),
    action: async (machineContext, userContext) => {
      await undoWrapper(
        userContext,
        getRowsInSection('Family members', 'Location'),
        FacebookSettingKey.ABOUT_FAMILY,
      )

      await undoWrapper(
        userContext,
        getRowsInSection('Status', 'Location'),
        FacebookSettingKey.ABOUT_RELATIONSHIPS,
      )
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
